import { IsNotEmpty, IsString, IsOptional, IsBoolean, IsEnum, MaxLength, IsUUID, ValidateNested } from 'class-validator';
import { StorageType } from '../../../../enums/clients/storage-type.enum';
import { Type } from 'class-transformer';

export class WarehouseContactRequestDto {
    @IsNotEmpty()
    @IsUUID()
    contactId: string;

    @IsOptional()
    @IsString()
    @MaxLength(255)
    roleDescription?: string;

    @IsOptional()
    @IsString()
    @MaxLength(255)
    context?: string;
}

export class CreateWarehouseDetailsRequestDto {
    /**
     * Reference to the organization that owns the warehouse
     * @example '123e4567-e89b-12d3-a456-426614174000'
     */
    @IsOptional()
    @IsUUID()
    organizationId?: string;

    /**
     * Name of the warehouse facility
     * @example 'Main Distribution Center'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    warehouseName?: string;

    /**
     * Physical address of the warehouse
     * @example '456 Storage Ave, Industrial District, Toronto, ON'
     */
    @IsOptional()
    @IsString()
    @MaxLength(500)
    warehouseAddress?: string;

    /**
     * Website URL of the warehouse facility
     * @example 'www.warehouse-solutions.com'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    warehouseWebsite?: string;

    /**
     * Contact person ID for the warehouse
     * @example '123e4567-e89b-12d3-a456-426614174003'
     */
    @IsOptional()
    @IsUUID()
    warehouseContactId?: string;

    @IsOptional()
    @ValidateNested({ each: true })
    @Type(() => WarehouseContactRequestDto)
    contacts?: WarehouseContactRequestDto[];

    /**
     * Type of storage arrangement (own warehouse, 3PL, etc.)
     * @example 'OWN_WAREHOUSE'
     */
    @IsOptional()
    @IsEnum(StorageType)
    storageType?: StorageType;

    /**
     * Whether the warehouse has security measures in place
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    hasSecurity?: boolean;

    /**
     * Whether the warehouse has CCTV surveillance
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    hasCctv?: boolean;

    /**
     * Whether warehouse is on Client/Importer DEL annex
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    clientImporterDelAnnex?: boolean;

    /**
     * Whether the 3PL has its own Quality Management System
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    hasOwnQms?: boolean;

    /**
     * Whether the 3PL uses Q&C Quality Management System
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    usesQncQms?: boolean;

    /**
     * Vendor providing temperature monitoring services
     * @example 'TempTracker Inc'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    tempMonitoringVendor?: string;

    /**
     * Frequency of temperature monitoring reports
     * @example 'Monthly'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    tempMonitoringReportFrequency?: string;

    /**
     * Whether automated reports with alerts and alarms are emailed
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    tempMonitoringAutomatedAlerts?: boolean;

    /**
     * Whether client downloads and sends temperature reports
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    tempMonitoringClientDownloads?: boolean;

    /**
     * Whether temperature monitoring is reviewed during self inspection
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    tempMonitoringSelfInspection?: boolean;

    /**
     * Who performs warehouse sanitation
     * @example 'Warehouse staff'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    sanitationPerformedBy?: string;

    /**
     * Frequency of sanitation self-inspection
     * @example 'Quarterly'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    sanitationSelfInspectionFreq?: string;

    /**
     * Whether sanitation is reviewed during self inspection
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    sanitationSelfInspection?: boolean;

    /**
     * Whether client downloads and sends sanitation reports
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    sanitationClientDownloads?: boolean;

    /**
     * Vendor providing pest control services
     * @example 'Orkin'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    pestControlVendor?: string;

    /**
     * Frequency of pest control self-inspection
     * @example 'Monthly'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    pestControlSelfInspectionFreq?: string;

    /**
     * Whether pest control is reviewed during self inspection
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    pestControlSelfInspection?: boolean;

    /**
     * Whether client downloads and sends pest control reports
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    pestControlClientDownloads?: boolean;

    /**
     * Whether automated pest control reports with alerts and alarms are emailed
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    pestControlAutomatedAlerts?: boolean;

    /**
     * Whether the warehouse has temperature control
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    temperatureControlled?: boolean;

    /**
     * Temperature range maintained in the warehouse
     * @example '15-25°C'
     */
    @IsOptional()
    @IsString()
    temperatureRange?: string;

    /**
     * Whether the warehouse has GMP certification
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    gmpCertified?: boolean;

    /**
     * GMP certification number
     * @example 'GMP-2024-001'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    gmpCertificationNumber?: string;

    /**
     * Whether the warehouse has GDP certification
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    gdpCertified?: boolean;

    /**
     * GDP certification number
     * @example 'GDP-2024-001'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    gdpCertificationNumber?: string;

    /**
     * Special handling requirements for products
     * @example 'Cold chain storage for temperature-sensitive products'
     */
    @IsOptional()
    @IsString()
    specialHandlingRequirements?: string;

    /**
     * Additional notes about the warehouse
     * @example 'Temperature-controlled facility with specialized cold storage area'
     */
    @IsOptional()
    @IsString()
    notes?: string;
}
